import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { css } from "styled-components";

interface KingPinOpeningScreenProps {
    fields?: any;
}

interface KingPinOpeningScreenStyles {

    wrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    searchButton?: ButtonPresentationProps;
}

export const findKingPinStyles: KingPinOpeningScreenStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 24px;
            padding-bottom: 24px;
        `,
    },
    formWrapper: {
        css: css`
            width: 100%;
            max-width: 340px;
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    heading: {
        variant: "h2",
    },
    searchButton: {
        variant: "primary",
    },
};


const KingPinOpeningScreen: React.FC<KingPinOpeningScreenProps> = ({
    fields
}) => {
    const styles = findKingPinStyles;
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
    const [selectedMake, setSelectedMake] = useState("");
    const [selectedModel, setSelectedModel] = useState("");
    const [makesData, setMakesData] = useState<string[]>([]);
    const [modelsData, setModelsData] = useState<string[]>([]);

    const fetchMakes = async () => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findKingPin/makes`);
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setMakesData(data);

        } catch (error) {
            console.error("Error fetching makes:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const fetchModels = async (make: string) => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findKingPin/models?make=${make}`);
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setModelsData(data);

        } catch (error) {
            console.error("Error fetching models:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const search = () => {
        if (!selectedMake || !selectedModel) return;

        const params = new URLSearchParams({
            axleManufacturer: selectedMake,
            axelModel: selectedModel,
        });
        window.location.search = `?${params.toString()}`;
    }

    const handleManufacturerChange: SelectProps["onChange"] = event => {
        const selectedValue = event.target.value;
        setSelectedModel("");
        setSelectedMake(selectedValue);
        fetchModels(selectedValue);
    }

    const handleModelChange: SelectProps["onChange"] = event => {
        setSelectedModel(event.target.value);
    }

    useEffect(() => {
        fetchMakes();
    }, []);

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                        width: 100%;
                    `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Typography {...styles.heading} as="h2">
                    {translate("Find King Pin Kits")}
                </Typography>
                <StyledWrapper {...styles.formWrapper} className="form-wrapper">
                    <Select
                        label={translate("Truck/Axle Manufacturer")}
                        name="manufacturer"
                        onChange={handleManufacturerChange}
                    >
                        <option value="">{translate("Select Truck/Axle Manufacturer")}</option>
                        {makesData && makesData.map(s => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                    <Select
                        label={translate("Truck/Axle Model Number")}
                        name="model"
                        onChange={handleModelChange}
                        disabled={!selectedMake}
                    >
                        <option value="">{translate("Select Truck/Axle Model Number")}</option>
                        {modelsData && modelsData.map(s => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                    <Button {...styles.searchButton} onClick={search} disabled={!selectedMake || !selectedModel}>
                        {translate("GO")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    )
};

export default KingPinOpeningScreen;